"use client"

import { useEffect, useState } from "react"

// Create a variable outside the component to track if the script has been loaded
let scriptLoaded = false

const ElevenLabsConvai = () => {
    const [isWidgetReady, setIsWidgetReady] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        // Check if we're on mobile
        const checkMobile = () => {
            const mobile = window.innerWidth < 768
            setIsMobile(mobile)

            // If this is the initial load and we're on mobile, keep widget closed
            if (!isWidgetReady) {
                setIsOpen(!mobile)
            }
        }

        // Initial check
        checkMobile()

        // Add resize listener
        window.addEventListener("resize", checkMobile)

        // Function to load the script only once
        const loadScript = () => {
            if (scriptLoaded) return Promise.resolve()

            return new Promise((resolve, reject) => {
                // Check if script already exists
                if (document.querySelector('script[src="https://elevenlabs.io/convai-widget/index.js"]')) {
                    scriptLoaded = true
                    resolve()
                    return
                }

                // Create and add the script
                const script = document.createElement("script")
                script.src = "https://elevenlabs.io/convai-widget/index.js"
                script.async = true
                script.type = "text/javascript"

                script.onload = () => {
                    scriptLoaded = true
                    resolve()
                }

                script.onerror = (error) => {
                    reject(error)
                }

                document.body.appendChild(script)
            })
        }

        // Function to add the widget element
        const addWidget = () => {
            // Only add the widget if it doesn't already exist
            if (!document.querySelector("elevenlabs-convai")) {
                try {
                    // Create a div to hold our widget with responsive styling
                    const widgetContainer = document.createElement("div")
                    widgetContainer.id = "elevenlabs-convai-container"

                    // Add responsive styles
                    widgetContainer.style.position = "fixed"
                    widgetContainer.style.bottom = "20px"
                    widgetContainer.style.right = "20px"
                    widgetContainer.style.zIndex = "9999"
                    widgetContainer.style.maxWidth = "100%"
                    widgetContainer.style.transition = "all 0.3s ease"

                    // Check if mobile and set initial visibility
                    const isMobileView = window.innerWidth < 768
                    if (isMobileView) {
                        widgetContainer.style.display = "none"
                        widgetContainer.style.opacity = "0"
                        widgetContainer.style.transform = "translateY(20px)"
                    }

                    // Mobile-specific styles
                    if (isMobileView) {
                        widgetContainer.style.width = "calc(100% - 40px)"
                        widgetContainer.style.maxHeight = "80vh"
                    } else {
                        widgetContainer.style.maxWidth = "400px"
                    }

                    document.body.appendChild(widgetContainer)

                    // Use innerHTML to add the custom element
                    widgetContainer.innerHTML = '<elevenlabs-convai agent-id="agent_01jvwvbhq9e3kvd43adnmcxb93"></elevenlabs-convai>'

                    // Add custom styles to the shadow DOM elements when they're ready
                    const addCustomStyles = () => {
                        const widget = document.querySelector("elevenlabs-convai")
                        if (widget && widget.shadowRoot) {
                            const style = document.createElement("style")
                            style.textContent = `
                    :host {
                    width: 100% !important;
                    height: auto !important;
                    max-height: 80vh !important;
                    }
                    .convai-container {
                    width: 100% !important;
                    height: auto !important;
                    max-height: 80vh !important;
                    border-radius: 12px !important;
                    overflow: hidden !important;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
                    }
                    .convai-chat-window {
                    max-height: 70vh !important;
                    overflow-y: auto !important;
                    }
                    .convai-input-container {
                    padding: 10px !important;
                    }
                    @media (max-width: 767px) {
                    .convai-container {
                        border-radius: 10px !important;
                    }
                    .convai-chat-window {
                        max-height: 60vh !important;
                    }
                    }
                `
                            widget.shadowRoot.appendChild(style)
                        }
                    }

                    // Try to add styles immediately and also after a delay to ensure they're applied
                    setTimeout(addCustomStyles, 500)
                    setTimeout(addCustomStyles, 1500)

                    setIsWidgetReady(true)
                    // Only open by default on desktop
                    setIsOpen(!isMobileView)
                } catch (error) {
                    console.error("Error adding ElevenLabs widget:", error)
                }
            } else {
                setIsWidgetReady(true)
            }
        }

        // Load script then add widget
        loadScript()
            .then(() => {
                // Wait a bit to ensure the custom element is registered
                setTimeout(addWidget, 100)
            })
            .catch((error) => {
                console.error("Error loading ElevenLabs script:", error)
            })

        return () => {
            window.removeEventListener("resize", checkMobile)
        }
    }, [])

    // Toggle widget visibility
    useEffect(() => {
        const widgetContainer = document.getElementById("elevenlabs-convai-container")
        if (widgetContainer) {
            if (isOpen) {
                widgetContainer.style.display = "block"
                // Animate in
                setTimeout(() => {
                    widgetContainer.style.opacity = "1"
                    widgetContainer.style.transform = "translateY(0)"
                }, 10)
            } else {
                // Animate out
                widgetContainer.style.opacity = "0"
                widgetContainer.style.transform = "translateY(20px)"
                setTimeout(() => {
                    widgetContainer.style.display = "none"
                }, 300)
            }
        }
    }, [isOpen])

    // Chat toggle button for mobile
    return (
        <>
            {isWidgetReady && (
                <div className="fixed bottom-4 right-4 z-[10000] flex flex-col items-end gap-2">
                    {!isOpen && (
                        <button
                            onClick={() => setIsOpen(true)}
                            className="flex items-center justify-center rounded-full p-3 shadow-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white transition-all"
                            aria-label="Chat with AI"
                        >
                            <span className="hidden md:inline-block md:mr-2">Chat with AI</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                        </button>
                    )}
                    {isOpen && isMobile && (
                        <button
                            onClick={() => setIsOpen(false)}
                            className="flex items-center justify-center rounded-full p-2 shadow-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
                            aria-label="Close chat"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    )}
                </div>
            )}
        </>
    )
}

export default ElevenLabsConvai
